/**
 * SnippetsSettings.tsx
 * Review Snippets widget specific settings component
 * Path: components/admin-tabs/reviews/display-settings/SnippetsSettings.tsx
 */

import React from 'react';
import { WidgetSettings } from '../../../../types';

interface SnippetsSettingsProps {
  settings: Partial<WidgetSettings>;
  onUpdate: (updates: Partial<WidgetSettings>) => void;
}

export const SnippetsSettings: React.FC<SnippetsSettingsProps> = ({ settings, onUpdate }) => {
  return (
    <div className="space-y-6">
      
      {/* Number of Photos */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Number of Photos to Display
        </label>
        <select
          value={settings.snippets_photos || 10}
          onChange={(e) => onUpdate({ snippets_photos: parseInt(e.target.value) })}
          className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value={8}>8 photos (4x2 grid)</option>
          <option value={10}>10 photos (5x2 grid)</option>
          <option value={12}>12 photos (4x3 grid)</option>
          <option value={15}>15 photos (5x3 grid)</option>
        </select>
        <p className="text-xs text-gray-500 mt-1">Customer photos shown in grid format</p>
      </div>

      {/* Layout Style */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Grid Layout
        </label>
        <div className="grid grid-cols-3 gap-3">
          <button
            onClick={() => onUpdate({ snippets_layout: '5x2' })}
            className={`p-4 border-2 rounded-lg text-sm font-medium transition-all ${
              (settings.snippets_layout || '5x2') === '5x2'
                ? 'border-blue-500 bg-blue-50 text-blue-700'
                : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            <div className="grid grid-cols-5 gap-0.5 mb-2 h-8">
              {[...Array(10)].map((_, i) => (
                <div key={i} className="bg-gray-300 rounded-sm"></div>
              ))}
            </div>
            5x2 Grid
          </button>

          <button
            onClick={() => onUpdate({ snippets_layout: '4x2' })}
            className={`p-4 border-2 rounded-lg text-sm font-medium transition-all ${
              settings.snippets_layout === '4x2'
                ? 'border-blue-500 bg-blue-50 text-blue-700'
                : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            <div className="grid grid-cols-4 gap-0.5 mb-2 h-8">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="bg-gray-300 rounded-sm"></div>
              ))}
            </div>
            4x2 Grid
          </button>

          <button
            onClick={() => onUpdate({ snippets_layout: '3x3' })}
            className={`p-4 border-2 rounded-lg text-sm font-medium transition-all ${
              settings.snippets_layout === '3x3'
                ? 'border-blue-500 bg-blue-50 text-blue-700'
                : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            <div className="grid grid-cols-3 gap-0.5 mb-2 h-8">
              {[...Array(9)].map((_, i) => (
                <div key={i} className="bg-gray-300 rounded-sm"></div>
              ))}
            </div>
            3x3 Grid
          </button>
        </div>
      </div>

      {/* Text Rotation Speed */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Text Rotation Speed
        </label>
        <input
          type="range"
          min="3000"
          max="8000"
          step="1000"
          value={settings.snippets_speed || 4000}
          onChange={(e) => onUpdate({ snippets_speed: parseInt(e.target.value) })}
          className="w-full"
        />
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>Fast (3s)</span>
          <span className="font-medium">{((settings.snippets_speed || 4000) / 1000).toFixed(0)}s</span>
          <span>Slow (8s)</span>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          How quickly review text rotates below photos
        </p>
      </div>

      {/* Photo Source */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Photo Source
        </label>
        <select
          value={settings.snippets_photo_source || 'all'}
          onChange={(e) => onUpdate({ snippets_photo_source: e.target.value })}
          className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="all">All reviews with photos</option>
          <option value="verified">Verified purchases only</option>
          <option value="top_rated">Top rated (4-5 stars) only</option>
          <option value="recent">Most recent with photos</option>
        </select>
        <p className="text-xs text-gray-500 mt-1">
          Which reviews to pull photos from
        </p>
      </div>

      {/* Display Options */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Text Display Options
        </label>
        <div className="space-y-3">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={settings.snippets_show_stars ?? true}
              onChange={(e) => onUpdate({ snippets_show_stars: e.target.checked })}
              className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
            />
            <div>
              <span className="text-sm text-gray-700">Show star rating</span>
              <p className="text-xs text-gray-500">Display stars in rotating review text</p>
            </div>
          </label>
          
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={settings.snippets_show_names ?? true}
              onChange={(e) => onUpdate({ snippets_show_names: e.target.checked })}
              className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
            />
            <div>
              <span className="text-sm text-gray-700">Show customer names</span>
              <p className="text-xs text-gray-500">Display reviewer name with text</p>
            </div>
          </label>
        </div>
      </div>

      {/* Placement Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex gap-3">
          <div className="flex-shrink-0">
            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-blue-900 mb-1">
              Auto-Placement
            </h4>
            <p className="text-xs text-blue-700">
              Review snippets will automatically appear below the "Buy Now" button on your product pages. If no "Buy Now" button is found, they'll appear below "Add to Cart".
            </p>
          </div>
        </div>
      </div>

      {/* Advanced: Border & Shadow */}
      <div>
        <details className="group">
          <summary className="flex items-center justify-between cursor-pointer text-sm font-medium text-gray-700 mb-2">
            <span>Advanced Styling</span>
            <svg className="w-4 h-4 text-gray-500 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </summary>
          
          <div className="mt-3 space-y-3 pl-4 border-l-2 border-gray-200">
            <div>
              <label className="block text-sm text-gray-700 mb-2">
                Container Style
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => onUpdate({ snippets_container_style: 'bordered' })}
                  className={`p-3 border-2 rounded-lg text-xs font-medium transition-all ${
                    (settings.snippets_container_style || 'bordered') === 'bordered'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <div className="w-full h-12 border-2 border-gray-300 rounded mb-1"></div>
                  Bordered
                </button>
                <button
                  onClick={() => onUpdate({ snippets_container_style: 'shadow' })}
                  className={`p-3 border-2 rounded-lg text-xs font-medium transition-all ${
                    settings.snippets_container_style === 'shadow'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <div className="w-full h-12 bg-white shadow-md rounded mb-1"></div>
                  Shadow
                </button>
              </div>
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.snippets_rounded_corners ?? true}
                onChange={(e) => onUpdate({ snippets_rounded_corners: e.target.checked })}
                className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Rounded corners</span>
            </label>
          </div>
        </details>
      </div>
    </div>
  );
};
